import { toDayEnd, toDayStart } from '@nx-smartmonkey/shared/helpers';
import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';

import { capacitorService } from '../../../../application/CapacitorService';
import { vehicleService } from '../../../../application/VehicleService';
import { AnalyticsSummary } from '../../../../domain/analytics/AnalyticsSummary';
import { useIsLogged } from '../../../../hooks/session/useIsLogged';
import { useErrorMiddleware } from '../../../../hooks/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../../hooks/useShowSnackbarMessage';

type UseRetrieveAnalyticsSummaryProps = {
  startDate: Date;
  endDate: Date;
};

type UseRetrieveAnalyticsSummaryResponseProps = ServiceResponse<Function, AnalyticsSummary>;

// Hooks act as 'Adapter' layer.
export const useRetrieveAnalyticsSummary = ({
  startDate,
  endDate,
}: UseRetrieveAnalyticsSummaryProps): UseRetrieveAnalyticsSummaryResponseProps => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { isLogged } = useIsLogged();

  const { isInitialLoading, isFetching, isSuccess, error, data, refetch } = useQuery(
    [`analytics_summary`],
    () =>
      vehicleService.retrieveAnalyticsSummary({
        startDate: toDayStart(startDate),
        endDate: toDayEnd(endDate),
      }),
    {
      enabled: Boolean(isLogged),
      refetchOnWindowFocus: capacitorService.isNative(),
      onError: (err: any) => {
        console.info(`Retrieve Analytics Summary Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error,
    success: isSuccess,
    data,
    run: refetch,
    isLoading: isInitialLoading || isFetching,
  };
};
